// UC-170: modal for bulk Variant identifier mapping CSV upload

import React, { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ArrowRight, Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import {
  identifierGroupValues,
  recalculateOpenPosForSkus,
} from '@/shared/variantMapping/recalculateOpenPoFunding';
import {
  parseMappingCsv,
  classifyMappingCsvImport,
  buildFailureSummary,
  groupReplacementsByRow,
  type MappingCsvRow,
  type ProposedRemap,
  type ProposedReplacement,
} from '@/shared/variantMapping/mappingCsvImport';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { functionsErrorMessage } from '@/utils/functionsErrorMessage';

export interface UploadMappingsCsvModalProps {
  open: boolean;
  organisationId: string;
  onClose: () => void;
  onImported?: () => void;
}

type PanelState = 'empty' | 'review' | 'remap' | 'blocking' | 'success';

const PREVIEW_HEADER_HEIGHT_PX = 48;
const PREVIEW_ROW_HEIGHT_PX = 53;
const PREVIEW_VISIBLE_ROWS = 10;
const HEADER_MAX_CHARS = 40;

export function displayHeader(header: string): string {
  const chars = Array.from(header);
  return chars.length > HEADER_MAX_CHARS
    ? `${chars.slice(0, HEADER_MAX_CHARS).join('')}…`
    : header;
}

function isHeaderTruncated(header: string): boolean {
  return Array.from(header).length > HEADER_MAX_CHARS;
}

interface SuccessPayload {
  rowsProcessed: number;
  newMasterProductsCreated: number;
  existingMasterProductsUpdated: number;
  identifierObservationsInserted: number;
  productLinksCreatedOrUpdated: number;
}

interface BlockingPayload {
  rowsScanned: number;
  blockingRows: number;
  errorTypeByRow: Array<{
    row: number;
    errorType: string;
    identifierType: string;
    identifierValue: string;
    reason: string;
  }>;
  message: string;
}

const UploadMappingsCsvModal: React.FC<UploadMappingsCsvModalProps> = ({
  open,
  organisationId,
  onClose,
  onImported,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<MappingCsvRow[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [panel, setPanel] = useState<PanelState>('empty');
  const [replacements, setReplacements] = useState<ProposedReplacement[]>([]);
  const [blocking, setBlocking] = useState<BlockingPayload | null>(null);
  const [success, setSuccess] = useState<SuccessPayload | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);

  const reset = () => {
    setHeaders([]);
    setRows([]);
    setSelected([]);
    setPanel('empty');
    setReplacements([]);
    setBlocking(null);
    setSuccess(null);
    setSubmitting(false);
    setFileName(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    const text = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(reader.error ?? new Error('Failed to read CSV'));
      reader.readAsText(file);
    });
    const parsed = parseMappingCsv(text);
    setHeaders(parsed.headers);
    setRows(parsed.rows);
    setSelected([]);
    setReplacements([]);
    setBlocking(null);
    setSuccess(null);
    setFileName(file.name);
    setPanel(parsed.headers.length > 0 ? 'review' : 'empty');
  };

  const toggleColumn = (columnIndex: number, checked: boolean) => {
    setSelected((prev) => (checked ? [...prev, columnIndex] : prev.filter((index) => index !== columnIndex)));
  };

  const confirmDisabled = selected.length === 0 || submitting || panel === 'success';

  const invokeImport = async (proceed: boolean) => {
    setSubmitting(true);
    try {
      const local = classifyMappingCsvImport({
        rows,
        headers,
        identifierColumnIndexes: selected,
        existingLinks: [],
      });
      if (local.kind === 'blocking') {
        const summary = buildFailureSummary(local);
        setBlocking({
          rowsScanned: summary.rowsScanned,
          blockingRows: summary.blockingRows,
          errorTypeByRow: summary.errorTypeByRow,
          message: summary.message,
        });
        setPanel('blocking');
        return;
      }

      const importedValues = rows.flatMap((row) =>
        selected.map((columnIndex) => row.cells[columnIndex] ?? '').filter((value) => value.trim() !== ''),
      );
      // Read before the import, because a code the import moves away takes the
      // old group's membership with it: afterwards there is no way to learn
      // which orders used to be funded alongside it.
      const groupBefore = await identifierGroupValues(organisationId, importedValues);

      const { data } = await invokeFunction<Record<string, unknown>>(supabase, 'import-variant-mapping-csv-v1', {
        body: {
          organisation_id: organisationId,
          headers,
          identifierColumnIndexes: selected,
          rows,
          proceed,
          fileName,
        },
      });

      const type = typeof data?.type === 'string' ? data.type : '';
      if (type.includes('remap-confirmation-required')) {
        const nextRemaps = (data?.remaps as ProposedRemap[] | undefined) ?? [];
        const nextReplacements = (data?.replacements as ProposedReplacement[] | undefined) ?? [];
        setReplacements(
          nextReplacements.length > 0
            ? nextReplacements
            : nextRemaps.map((remap) => ({ ...remap, isRemap: true })),
        );
        setPanel('remap');
        return;
      }
      if (type.includes('import-conflict')) {
        setBlocking({
          rowsScanned: Number(data?.rowsScanned ?? 0),
          blockingRows: Number(data?.blockingRows ?? 0),
          errorTypeByRow: (data?.errorTypeByRow as BlockingPayload['errorTypeByRow'] | undefined) ?? [],
          message: String(data?.message ?? data?.detail ?? 'No rows were imported.'),
        });
        setPanel('blocking');
        return;
      }

      const metrics = (data?.metrics ?? {}) as Partial<SuccessPayload>;
      setSuccess({
        rowsProcessed: Number(metrics.rowsProcessed ?? 0),
        newMasterProductsCreated: Number(metrics.newMasterProductsCreated ?? 0),
        existingMasterProductsUpdated: Number(metrics.existingMasterProductsUpdated ?? 0),
        identifierObservationsInserted: Number(metrics.identifierObservationsInserted ?? 0),
        productLinksCreatedOrUpdated: Number(metrics.productLinksCreatedOrUpdated ?? 0),
      });
      setPanel('success');

      // FIG-438 rule 4b: a mapping CSV upload is the second of the two moments
      // PO codes may be matched. Any open PO line carrying one of the imported
      // values is matched again and refunded. Fire and forget -- the import has
      // already succeeded, and a failed refresh is logged by the helper.
      //
      // Both groups, not just the printed values. A line is funded from its
      // whole Variant group, so moving one code changes the figures of every
      // sibling that shared its Variant -- orders carrying only a sibling would
      // otherwise keep funding that still counted the departed code's stock and
      // sales. `groupBefore` covers the siblings left behind, the lookup below
      // covers the group the import created.
      void (async () => {
        const groupAfter = await identifierGroupValues(organisationId, importedValues);
        await recalculateOpenPosForSkus(
          organisationId,
          [...new Set([...importedValues, ...groupBefore, ...groupAfter])],
          { matchPoCodes: true },
        );
      })();

      onImported?.();
    } catch (err: unknown) {
      const context = (err as { context?: unknown } | null)?.context;
      let problemBody: Record<string, unknown> | null = null;
      if (context instanceof Response) {
        problemBody = (await context.clone().json().catch(() => null)) as Record<string, unknown> | null;
      } else if (context && typeof context === 'object' && typeof (context as { json?: unknown }).json === 'function') {
        problemBody = (await (context as Response).clone().json().catch(() => null)) as Record<string, unknown> | null;
      }
      const type = typeof problemBody?.type === 'string' ? problemBody.type : '';
      if (type.includes('import-conflict')) {
        setBlocking({
          rowsScanned: Number(problemBody?.rowsScanned ?? 0),
          blockingRows: Number(problemBody?.blockingRows ?? 0),
          errorTypeByRow: (problemBody?.errorTypeByRow as BlockingPayload['errorTypeByRow']) ?? [],
          message: String(problemBody?.message ?? problemBody?.detail ?? 'No rows were imported.'),
        });
        setPanel('blocking');
        return;
      }
      toast.error(await functionsErrorMessage(err, 'Failed to import mapping CSV'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleClose(); }}>
      <DialogContent
        className={panel === 'empty' ? 'max-w-lg' : 'max-w-4xl max-h-[85vh] overflow-y-auto'}
        aria-describedby="upload-mappings-csv-description"
      >
        <DialogHeader>
          <DialogTitle>Upload mappings CSV</DialogTitle>
          <DialogDescription id="upload-mappings-csv-description">
            Upload variant bulk identifiers. Each selected column becomes an identifier type; each cell is an identifier value for that row&apos;s Variant.
          </DialogDescription>
        </DialogHeader>

        {panel === 'empty' && (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>CSV file</Label>
              <div
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Click to select a CSV file</p>
                <p className="text-xs text-muted-foreground mt-1">Must be a .csv file</p>
              </div>
              <input
                id="upload-mappings-csv-file"
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(event) => void handleFile(event.target.files?.[0])}
              />
            </div>
          </div>
        )}

        {panel === 'review' && (
          <div
            className="overflow-auto rounded-md border"
            data-testid="mapping-csv-preview"
            style={{ maxHeight: PREVIEW_HEADER_HEIGHT_PX + PREVIEW_ROW_HEIGHT_PX * PREVIEW_VISIBLE_ROWS }}
          >
            <TooltipProvider delayDuration={300}>
              <Table containerClassName="overflow-visible">
                <TableHeader className="sticky top-0 z-10 bg-background">
                  <TableRow>
                    {headers.map((header, columnIndex) => {
                      const label = displayHeader(header);
                      const truncated = isHeaderTruncated(header);
                      return (
                        <TableHead key={`head-${columnIndex}`}>
                          <Label
                            htmlFor={`identifier-col-${columnIndex}`}
                            className="flex items-center gap-2 cursor-pointer whitespace-nowrap"
                          >
                            <Checkbox
                              id={`identifier-col-${columnIndex}`}
                              aria-label={header}
                              checked={selected.includes(columnIndex)}
                              onCheckedChange={(value) => toggleColumn(columnIndex, value === true)}
                            />
                            {truncated ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>{label}</span>
                                </TooltipTrigger>
                                <TooltipContent className="z-[60] max-w-sm break-all">
                                  {header}
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              label
                            )}
                          </Label>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow key={`row-${idx}`}>
                      {headers.map((_, columnIndex) => (
                        <TableCell key={`${idx}-${columnIndex}`}>{row.cells[columnIndex] ?? ''}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TooltipProvider>
          </div>
        )}

        {panel === 'remap' && (
          <ExistingMappingsReplacementPrompt
            replacements={replacements}
            submitting={submitting}
            onProceed={() => void invokeImport(true)}
          />
        )}

        {panel === 'blocking' && blocking && (
          <Alert variant="destructive">
            <AlertTitle>Import blocked</AlertTitle>
            <AlertDescription>
              <p>Rows scanned: {blocking.rowsScanned}</p>
              <p>Blocking rows: {blocking.blockingRows}</p>
              <ul className="mt-2 max-h-64 list-disc space-y-1 overflow-y-auto pl-5">
                {blocking.errorTypeByRow.map((row) => (
                  <li key={`${row.row}-${row.identifierType}-${row.identifierValue}`}>
                    Row {row.row}: {row.errorType} — {row.identifierType}={row.identifierValue}. {row.reason}
                  </li>
                ))}
              </ul>
              <p className="mt-2 font-medium">{blocking.message}</p>
            </AlertDescription>
          </Alert>
        )}

        {panel === 'success' && success && (
          <Alert>
            <AlertTitle>Import complete</AlertTitle>
            <AlertDescription>
              <p>Rows processed: {success.rowsProcessed}</p>
              <p>New Master Products created: {success.newMasterProductsCreated}</p>
              <p>Existing Master Products updated: {success.existingMasterProductsUpdated}</p>
              <p>Identifier observations inserted: {success.identifierObservationsInserted}</p>
              <p>Product Links created or updated: {success.productLinksCreatedOrUpdated}</p>
              <p>No blocking issues were found.</p>
            </AlertDescription>
          </Alert>
        )}

        {(panel === 'empty' || panel === 'review') && (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            {panel === 'review' && (
              <Button
                type="button"
                disabled={confirmDisabled}
                onClick={() => void invokeImport(false)}
              >
                Confirm variant mapping
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

const AFFECTED_ROW_HEIGHT_PX = 36;
const AFFECTED_ROW_VISIBLE_COUNT = 5;

function ExistingMappingsReplacementPrompt({
  replacements,
  submitting,
  onProceed,
}: {
  replacements: ProposedReplacement[];
  submitting: boolean;
  onProceed: () => void;
}) {
  const affectedRows = useMemo(() => groupReplacementsByRow(replacements), [replacements]);
  const affectedCount = affectedRows.length;
  const accordionOpenByDefault = affectedCount < AFFECTED_ROW_VISIBLE_COUNT;

  return (
    <Alert>
      <AlertTitle>Existing mappings will be replaced</AlertTitle>
      <AlertDescription>
        <p>
          This CSV includes identifiers that already map to Variants. Confirming will replace those
          existing mappings with the values from the file.
        </p>
        <Accordion
          type="single"
          collapsible
          defaultValue={accordionOpenByDefault ? 'affected-rows' : undefined}
          className="mt-3 rounded-md border px-3"
        >
          <AccordionItem value="affected-rows" className="border-b-0">
            <AccordionTrigger className="py-2 text-sm hover:no-underline">
              Affected rows ({affectedCount})
            </AccordionTrigger>
            <AccordionContent>
              <div
                className="overflow-y-auto"
                style={{ maxHeight: AFFECTED_ROW_HEIGHT_PX * AFFECTED_ROW_VISIBLE_COUNT }}
              >
                <ul>
                  {affectedRows.map(({ rowIndex, items }) =>
                    items.map((item, itemIndex) => (
                      <li
                        key={`affected-${rowIndex}-${itemIndex}`}
                        className="flex items-center gap-2 border-b border-border px-1 text-sm last:border-b-0"
                        style={{ height: AFFECTED_ROW_HEIGHT_PX }}
                      >
                        <span className="w-12 shrink-0 text-muted-foreground">
                          {itemIndex === 0 ? `Row ${rowIndex + 1}` : ''}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-center">
                          <span className="font-bold">{item.identifierType}</span>
                          : {item.existingIdentifierValue || item.identifierValue}
                        </span>
                        <ArrowRight
                          aria-hidden
                          className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                        />
                        <span className="sr-only">to</span>
                        <span className="min-w-0 flex-1 truncate text-center">
                          <span className="font-bold">{item.identifierType}</span>
                          : {item.identifierValue}
                        </span>
                      </li>
                    )),
                  )}
                </ul>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <Button className="mt-4" type="button" disabled={submitting} onClick={onProceed}>
          Proceed
        </Button>
      </AlertDescription>
    </Alert>
  );
}

export default UploadMappingsCsvModal;
